import { useState } from "react";
import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { api } from "@/lib/api";
import { t } from "@/i18n";

/**
 * Sign-in / sign-up page.
 *
 * Local dev: email+password (no Google config needed — this is how you log
 * in while running locally without Google OAuth credentials).
 * Production: "Sign in with Google" → /api/auth/sign-in/google (better-auth).
 */
export const Route = createFileRoute("/sign-in")({
  component: SignInPage,
});

function SignInPage() {
  const navigate = useNavigate();
  const router = useRouter();
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState("teacher@example.com");
  const [name, setName] = useState("Teacher");
  const [password, setPassword] = useState("devpassword");
  const [submitting, setSubmitting] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { email, password, ...(isSignUp ? { name } : {}) };
      const path = isSignUp ? "/api/auth/sign-up/email" : "/api/auth/sign-in/email";
      await api(path, { method: "POST", body: JSON.stringify(payload) });
      // better-auth set the session cookie on this response (credentials: include).
      // Invalidate the router so pending loads refresh, then go home.
      router.invalidate();
      navigate({ to: "/app/dashboard" });
    } catch (err: any) {
      // better-auth surfaces human-readable errors here.
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  // better-auth social endpoints are POST-only: ask for the Google authorize
  // URL, then send the browser there (its redirect_uri is the /api/auth/
  // callback/google URI registered in Google Cloud).
  async function signInWithGoogle() {
    setGoogleBusy(true);
    try {
      const res = await fetch("/api/auth/sign-in/social", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ provider: "google", callbackURL: "/app/dashboard" }),
      });
      const data = (await res.json()) as { url?: string };
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      alert(t("auth.googleNotConfigured"));
    } catch {
      alert(t("auth.googleNotConfigured"));
    } finally {
      setGoogleBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 px-4">
      <div className="w-full max-w-sm space-y-6 rounded-xl border bg-card p-8">
        <h1 className="text-center text-2xl font-bold">{t("app.name")}</h1>
        <form className="space-y-4" onSubmit={handleSubmit}>
          {isSignUp && (
            <input
              className="w-full rounded-md border px-3 py-2"
              placeholder={t("auth.name")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          )}
          <input
            type="email"
            className="w-full rounded-md border px-3 py-2"
            placeholder={t("auth.email")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <input
            type="password"
            className="w-full rounded-md border px-3 py-2"
            placeholder={t("auth.password")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-md bg-primary py-2 text-primary-foreground hover:bg-primary/90"
          >
            {submitting ? t("app.loading") : isSignUp ? t("auth.signUp") : t("auth.signIn")}
          </button>
        </form>

        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t"></div>
          </div>
          <span className="absolute inset-x-0 top-1/2 -translate-y-1/2 bg-card px-2 text-xs text-muted-foreground">
            {t("auth.or")}
          </span>
        </div>

        <button
          type="button"
          onClick={() => void signInWithGoogle()}
          disabled={googleBusy}
          className="flex w-full items-center justify-center gap-2 rounded-md border py-2 hover:bg-muted disabled:opacity-50"
        >
          {t("home.ctaGoogle")}
        </button>

        <p className="text-center text-sm text-muted-foreground">
          <button
            type="button"
            className="underline"
            onClick={() => setIsSignUp(!isSignUp)}
          >
            {isSignUp ? t("auth.switchToSignIn") : t("auth.switchToSignUp")}
          </button>
        </p>
      </div>
    </div>
  );
}

